import { SPACING_L_XS } from '@audi/audi-ui-design-tokens';
import { useEffect, useRef } from 'react';
import { SliderAction } from '~/components/slider-panel/components/slider-action';
import { SliderCatalogueHeadline } from '~/components/slider-panel/components/slider-catalogue-headline';
import { SliderItems } from '~/components/slider-panel/components/slider-items';
import { effectActiveItem } from '~/components/slider-panel/effects/effect-active-item';
import { useCatalogue } from '~/components/slider-panel/hooks/use-catalogue';
import { useSlider } from '~/components/slider-panel/hooks/use-slider';
import { useSwipeHandlers } from '~/components/slider-panel/hooks/use-swipe-handlers';
import { SliderPanelProps } from '~/components/slider-panel/models/slider-panel-props';
import {
	SliderPanelCanvasInnerStyle,
	SliderPanelCanvasStyle,
	SliderPanelStyle,
} from '~/components/slider-panel/styles/slider-panel-style';

export function SlidePanel({
	className,
	catalogueHeadline,
	children,
	sliderItemWidth,
	loaderOnIndex = -1,
	activeIndex = 0,
	isNavigationVisible = true,
	navigationLeftPosition = SPACING_L_XS,
	isBlocked = false,
	sliderSpaceBetween = 40,
}: SliderPanelProps) {
	const canvasInner = useRef<HTMLDivElement>(null);
	const {
		activeItem,
		moveSliderLeftPosition,
		hasNavigation,
		setActiveItem,
		moveSliderToIndex,
		slideBack,
		slideNext,
		getSliderLeftPosition,
		setHasNavigation,
	} = useSlider(isNavigationVisible, sliderItemWidth, activeIndex, children.length);
	const { isSliderExpanded, collapsCatalogue, expandCatalogue } = useCatalogue(
		getSliderLeftPosition,
		canvasInner.current,
	);

	useEffect(() => {
		// Reset slider to index 0 when new items are being loaded
		moveSliderToIndex(0);
	}, [children.length]);

	useEffect(() => {
		const shouldShowNavigator = () => {
			const viewNavigation =
				isNavigationVisible &&
				children.length > 1 &&
				!!canvasInner.current &&
				!!canvasInner.current.parentElement &&
				canvasInner.current.offsetWidth > canvasInner.current.parentElement.offsetWidth;
			setHasNavigation(viewNavigation);
		};

		window.addEventListener('resize', shouldShowNavigator);
		setHasNavigation(isNavigationVisible && children.length > 1);

		return () => window.removeEventListener('resize', shouldShowNavigator);
	}, [isNavigationVisible, setHasNavigation, children.length]);

	useEffect(
		() => effectActiveItem(activeIndex, isSliderExpanded, hasNavigation, setActiveItem, moveSliderToIndex),
		[activeIndex, isSliderExpanded, hasNavigation, setActiveItem, moveSliderToIndex],
	);

	const slideBackWrapped = () => {
		if (isSliderExpanded) return;
		slideBack();
	};
	const slideNextWrapped = () => {
		if (isSliderExpanded) return;
		slideNext();
	};

	return (
		<SliderPanelStyle className={`slider-panel ${className}`}>
			<SliderPanelCanvasStyle
				className={`slider-panel-canvas ${isSliderExpanded && 'expanded'}`}
				{...useSwipeHandlers({ slideBack: slideBackWrapped, slideNext: slideNextWrapped })}>
				<SliderPanelCanvasInnerStyle
					ref={canvasInner}
					sliderSpaceBetween={sliderSpaceBetween}
					className={`slider-panel-canvas-inner ${isSliderExpanded && 'expanded'}`}
					style={{ left: `${moveSliderLeftPosition}px` }}>
					<SliderCatalogueHeadline
						spaceBetween={sliderSpaceBetween}
						headline={catalogueHeadline}
						isVisible={isSliderExpanded}
						collapsCatalogue={collapsCatalogue}
					/>
					<SliderItems
						sliderItemWidth={sliderItemWidth}
						loaderOnIndex={loaderOnIndex}
						isSliderExpanded={isSliderExpanded}
						isSliderBlocked={isBlocked}>
						{children}
					</SliderItems>
				</SliderPanelCanvasInnerStyle>
			</SliderPanelCanvasStyle>
			{hasNavigation && (
				<SliderAction
					slideBack={slideBackWrapped}
					slideNext={slideNextWrapped}
					expandCatalogue={expandCatalogue}
					navigationLeftPosition={navigationLeftPosition}
					active={activeItem + 1}
					sliderLength={children.length}
				/>
			)}
		</SliderPanelStyle>
	);
}
